import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useCreateUserStore } from 'Application/store/useUserStore';
import { toast } from 'sonner';
import { UpdateUser, UpdateUserRequest, UserRole, UsersManagementProps } from 'Application/models';
import { getUserRoleNameFromStorage, retrieveEncryptedData } from 'Application/utils/AppSessionStorage';
import { ThemeMode } from 'config';
import { useRegionStore } from 'Application/store';

interface EditUserProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    user: UpdateUser;
    usersManagment: UsersManagementProps;
}

const EditUser: React.FC<EditUserProps> = ({ isOpen, setIsOpen, user, usersManagment }) => {
    const { searchTerm, rowsPerPage, currentPage, countryValue, searchInput, setSearchInput, setCurrentPage } = usersManagment;

    const { editUser, editUserRole, isLoading, getInternalUsers } = useCreateUserStore();
    const { regions, getRegions, isLoading: isRegionsLoading } = useRegionStore();

    const loggedInUserRole = getUserRoleNameFromStorage();

    const [userRole, setUserRole] = useState<UserRole>(user.role);
    const [formData, setFormData] = useState({
        firstName: '',
        otherNames: '',
        emailAddress: '',
        regionName: ''
    });

    useEffect(() => {
        loggedInUserRole === UserRole.ROLE_ADMINISTRATOR && getRegions();
    }, []);

    useEffect(() => {
        setFormData({
            firstName: user.firstName,
            otherNames: user.otherNames,
            emailAddress: user.emailAddress,
            regionName: loggedInUserRole === UserRole.ROLE_ADMINISTRATOR ? (user.regionName ?? '') : ''
        });

        setUserRole(user.role);
    }, [user]);

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    const roleName = retrieveEncryptedData();

    const handleChange = (e: SelectChangeEvent<UserRole>) => {
        setUserRole(e.target.value as UserRole);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleRegionInputChange = (event: SelectChangeEvent<string>) => {
        const { value } = event.target;
        setFormData({
            ...formData,
            regionName: value
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const userData: UpdateUserRequest =
            loggedInUserRole === UserRole.ROLE_ADMINISTRATOR
                ? formData
                : {
                      firstName: formData.firstName,
                      otherNames: formData.otherNames,
                      emailAddress: formData.emailAddress
                  };

        await editUser(user.id, userData);
        await editUserRole(user.id, userRole);

        searchInput !== ''
            ? setSearchInput('')
            : currentPage !== 1
              ? setCurrentPage(1)
              : getInternalUsers(searchTerm, currentPage, rowsPerPage, countryValue);
        // await getInternalUsers();
        toast.success('User updated successfully');
        setIsOpen(false);
        // clear the form data
        setFormData({
            firstName: '',
            otherNames: '',
            emailAddress: '',
            regionName: ''
        });
        setUserRole(UserRole.ROLE_ADMINISTRATOR); // reset the role section
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Edit User Details</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Provide the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Full Name
                        </Typography>
                        <TextField
                            placeholder="Enter Full Name"
                            required
                            name="firstName"
                            variant="outlined"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            fullWidth
                            disabled={isLoading}
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Other Name
                        </Typography>
                        <TextField
                            placeholder="Enter Full Name"
                            required
                            name="otherNames"
                            variant="outlined"
                            value={formData.otherNames}
                            onChange={handleInputChange}
                            fullWidth
                            disabled={isLoading}
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Email
                        </Typography>
                        <TextField
                            placeholder="Enter Email"
                            required
                            name="emailAddress"
                            variant="outlined"
                            type="email"
                            value={formData.emailAddress}
                            onChange={handleInputChange}
                            fullWidth
                            disabled={isLoading}
                        />
                    </div>
                    {loggedInUserRole === UserRole.ROLE_ADMINISTRATOR && (
                        <div style={{ paddingBottom: '1rem' }}>
                            <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                Region
                            </Typography>
                            <Select value={formData.regionName} fullWidth onChange={handleRegionInputChange}>
                                {isRegionsLoading ? (
                                    <MenuItem disabled>Loading...</MenuItem>
                                ) : (
                                    regions?.map((region) => {
                                        const { name, regionID } = region;

                                        return (
                                            <MenuItem key={regionID} value={name}>
                                                {name}
                                            </MenuItem>
                                        );
                                    })
                                )}
                            </Select>
                        </div>
                    )}
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Select A Role
                        </Typography>
                        {/* <Select value={userRole} onChange={handleChange} fullWidth>
                            {roleName === "ROLE_ADMINISTRATOR" ? (
                                <>
                                    <MenuItem value={UserRole.ROLE_ADMINISTRATOR}>Administrator</MenuItem>
                                    <MenuItem value={UserRole.ROLE_AGENT}>Agent</MenuItem>
                                </>
                            ) : (
                                <MenuItem value={UserRole.ROLE_AGENT}>Agent</MenuItem>
                            )}
                            </Select> */}
                        {roleName === UserRole.ROLE_ADMINISTRATOR ? (
                            <Select value={userRole} onChange={handleChange} fullWidth disabled={isLoading}>
                                <MenuItem value={UserRole.ROLE_ADMINISTRATOR}>Administrator</MenuItem>
                                <MenuItem value={UserRole.ROLE_REGIONAL_ADMIN}>Regional Administrator</MenuItem>
                                <MenuItem value={UserRole.ROLE_UNDER_WRITER}>Underwriter</MenuItem>
                                <MenuItem value={UserRole.ROLE_ISC}>ISC</MenuItem>
                                <MenuItem value={UserRole.ROLE_CSE}>CSE</MenuItem>
                            </Select>
                        ) : (
                            <Select value={userRole} onChange={handleChange} fullWidth>
                                <MenuItem value={UserRole.ROLE_BRANCH_MANAGER}>Branch manager</MenuItem>
                                <MenuItem value={UserRole.ROLE_AGENT}>Agent</MenuItem>
                            </Select>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                            disabled={isLoading} // Disable button while loading
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading} // Disable button while loading
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditUser;
